import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import { CALENDLY_URL, SERVICES } from "../data/content";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <main className="pt-32 pb-16" data-testid={`service-page-${slug}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp y={12}>
          <Link to="/#services" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-green transition-colors duration-200 mb-10" data-testid="service-back-link">
            <ArrowLeft size={13} /> All services
          </Link>
        </FadeUp>
        <p className="font-mono text-lg uppercase tracking-[0.25em] text-ink-mid mb-5">Service area {service.num}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ink mb-8">
          <RevealLine animateOnLoad>{service.headline[0]}</RevealLine>
          <RevealLine animateOnLoad delay={0.12}><span className="italic text-green">{service.headline[1]}</span></RevealLine>
        </h1>
        <FadeUp className="max-w-2xl text-ink-mid leading-relaxed text-lg mb-10">{service.sub}</FadeUp>
        <FadeUp delay={0.1} className="flex flex-wrap gap-2 mb-10">
          {service.allPills.map((p) => (
            <span key={p} className="text-[11px] font-mono uppercase tracking-wider bg-green-lt text-green px-4 py-1.5 rounded-full">{p}</span>
          ))}
        </FadeUp>
        <FadeUp delay={0.15} className="mb-16">
          <img src={`/images/service-${slug}.png`} alt={`${service.title} — illustration`} className="w-full h-64 sm:h-80 object-cover rounded-2xl" data-testid="service-hero-image" />
        </FadeUp>

        <div className="border-t border-rule">
          {service.services.map((s, i) => (
            <FadeUp key={s.name} delay={i * 0.05}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-10 border-b border-rule group" data-testid={`service-item-${i}`}>
                <div className="lg:col-span-1 font-mono text-xs text-ink-soft pt-2 tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</div>
                <h2 className="lg:col-span-4 font-serif text-2xl text-ink group-hover:text-green transition-colors duration-300">{s.name}</h2>
                <p className="lg:col-span-7 text-sm text-ink-mid leading-relaxed">{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-3xl text-ink mb-4">Talk to us about <span className="italic text-green">{service.title.toLowerCase()}.</span></h2>
            <p className="text-sm text-ink-mid leading-relaxed mb-8">Tell us where you are and what's not working. We'll come back with a point of view — not a pitch deck.</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="service-calendly-btn" className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-mid transition-colors duration-200">
              Book a free call <ArrowUpRight size={15} />
            </a>
          </div>
          <div className="lg:col-span-7 bg-white rounded-2xl p-8 lg:p-10">
            <EnquiryForm source={`service-${slug}`} compact />
          </div>
        </div>

        <div className="mt-16">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-soft mb-8">Also explore</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-related-${s.slug}`} className="group bg-white rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <span className="font-mono text-[10px] text-ink-soft tracking-[0.2em]">{s.num}</span>
                <h3 className="font-serif text-xl text-ink mt-3 mb-6 group-hover:text-green transition-colors duration-300">{s.title}</h3>
                <span className="inline-flex items-center gap-2 text-xs font-medium text-green">
                  View <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
